import { useEffect } from 'react';
import api from '@/shared/utils/api';
import { useApiQuery } from './useApiQuery';
import {
  applyTaxonomyOverride,
  JANUS_SENIORITIES,
  JANUS_DEPARTMENTS,
  type SeniorityEntry,
  type DepartmentEntry,
} from '@/shared/constants/janusTaxonomy';

export interface JanusTaxonomyPayload {
  seniorities: SeniorityEntry[];
  departments: DepartmentEntry[];
  updatedAt?: string | null;
}

/**
 * Loads the bilingual job taxonomy (cargos + departamentos, PT default + EN)
 * from the central operations-management catalog via the BFF
 * (`/api/v1/config/taxonomy`, public + cached). On success it refreshes the
 * shared label maps (`applyTaxonomyOverride`) so `getSeniorityLabel` /
 * `getDeptLabel` everywhere reflect catalog edits without a frontend deploy.
 *
 * Always resolves to a usable payload — falls back to the bundled constants
 * if the endpoint is unreachable, so the ICP selector / badges never break.
 */
export function useJanusTaxonomy() {
  const query = useApiQuery<JanusTaxonomyPayload>(
    ['config', 'taxonomy'],
    async () => {
      const { data } = await api.get('/config/taxonomy');
      return data as JanusTaxonomyPayload;
    },
    {
      // Catalog is public + cacheable; match the BFF 5-min cache window.
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 1,
    },
  );

  useEffect(() => {
    if (query.data) {
      applyTaxonomyOverride(query.data);
    }
  }, [query.data]);

  return {
    ...query,
    seniorities: query.data?.seniorities ?? (JANUS_SENIORITIES as SeniorityEntry[]),
    departments: query.data?.departments ?? (JANUS_DEPARTMENTS as DepartmentEntry[]),
  };
}
